import React, {type ReactNode, useEffect, useMemo, useRef} from 'react';

import cs from 'classnames';
import jquery from 'jquery';
import {colors} from 'js-libs/sweetcommons/styles';
import {Link} from 'react-router';
import styled from 'styled-components';

import {HighlightableTextWrapper} from 'app/comments/components/HighlightableTextWrapper';
import {CopyLinkButton} from 'app/components/general/CopyLinkButton';
import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {DocIsDraftBadge} from 'app/general/dashboards/DocIsDraftBadge';
import {ProcedureStepPrivateStatus} from 'app/general/doc-status/DocumentPrivateStatus';
import {
  HIGHLIGHT_PROCEDURE_STEP_TITLE_PREFIX,
  HIGHLIGHT_PROCESS_STEP_TITLE_PREFIX,
} from 'app/general/structures/UserContentContainer/utils/highlightUtils';
import {ImagesCarousel} from 'app/general/widgets/ImagesCarousel';
import {OrphanStepBadge} from 'app/procedures/components/OrphanStepBadge';
import {ProcedureStepFiles} from 'app/procedures/components/ProcedureStepFiles';
import {StyledStepContentSeparatorDiv} from 'app/procedures/components/structures';
import {COLLAPSE_DURATION_MS} from 'app/utils/constants';
import {gt} from 'app/utils/i18n';
import {isRightToLeftWrapper} from 'app/utils/rtl';

import {ProcedureStepStructure} from '../ProcedureStepStructure';

import {ProcedureAutomationStepType} from 'sweet-types/procedure/procedure-step';

import type {Ref} from 'app/general/types';
import type {ProcedureStepWithDiffType} from 'app/tasks/types';
import type {
  AnyStepType,
  CommentableContentType,
  DeletedProcessProcedureAsStepSerializedType,
  ProcedureContentStepType,
  ProcedureDecisionStepType,
  ProcedureEndingStepType,
  ProcessProcedureAsStepSerializedType,
  RegularProcessProcedureAsStepInTaskSerializedType,
} from 'sweet-types';

interface Props {
  index: number | string;
  step:
    | ProcedureStepWithDiffType
    | ProcedureContentStepType
    | ProcedureDecisionStepType
    | ProcedureAutomationStepType
    | ProcedureEndingStepType
    | DeletedProcessProcedureAsStepSerializedType
    | AnyStepType
    | ProcessProcedureAsStepSerializedType
    | RegularProcessProcedureAsStepInTaskSerializedType;
  collapsed: boolean;
  contentType: CommentableContentType;
  objectId?: number;
  versionId?: number;
  objectURL?: string;
  onToggleCollapse: (id: string, collapse: boolean) => unknown;
  warnIncomplete?: boolean;
  orphan?: boolean;
  actionButtons?: JSX.Element | null;
  fieldInputs?: JSX.Element | null;
  choicesList?: JSX.Element | null;
  automation?: JSX.Element | null;
  next?: JSX.Element;
  previous?: JSX.Element;
  showCopyLink?: boolean;
}

export function ProcedureStep({
  step,
  collapsed,
  onToggleCollapse,
  warnIncomplete = false,
  orphan,
  actionButtons,
  fieldInputs,
  choicesList,
  automation,
  next,
  previous,
  showCopyLink,
  objectId,
  versionId,
  objectURL,
  contentType,
}: Props) {
  const collapsableEl = useRef<HTMLDivElement | null>(null);
  const stepTitleRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    animateCollapse(collapsed, collapsableEl, true);
  }, []);

  useEffect(() => {
    animateCollapse(collapsed, collapsableEl);
  }, [collapsed]);

  const stepIsActiveProcedure =
    step.type === 'processprocedure' && !step.deleted;

  const content = step.type !== 'processprocedure' ? step.content || '' : '';
  const images = ('images' in step && step.images) || [];
  const embeds = ('embeds' in step && step.embeds) || [];
  const carousel = [...images, ...embeds];
  const title =
    step.title ||
    (step.type === 'processprocedure' && step.deleted
      ? gt('Deleted Procedure')
      : step.type === 'processprocedure'
      ? gt('Untitled Procedure')
      : step.type === 'automation'
      ? gt('Untitled Automation')
      : '');

  const isRTLText = useMemo(
    () => isRightToLeftWrapper(title || content || ''),
    [title, content]
  );

  const HIGHLIGHT_STEP_TITLE_PREFIX = {
    processversion: HIGHLIGHT_PROCESS_STEP_TITLE_PREFIX,
    procedureversion: HIGHLIGHT_PROCEDURE_STEP_TITLE_PREFIX,
  };

  return (
    <ProcedureStepStructure
      className={cs({
        'opacity-overlay': stepIsActiveProcedure && step.approved_at == null,
        'right-to-left-text': isRTLText,
      })}
      changedState={'changedState' in step ? step.changedState : undefined}
      movedFrom={'movedFrom' in step ? step.movedFrom : undefined}
      warnIncomplete={warnIncomplete}
      n={step.n}
      type={step.type || 'content'}
      beforeStepContent={<>{previous}</>}
      beforeTitleContent={
        <>
          {orphan && <OrphanStepBadge type={step.type} />}
          {stepIsActiveProcedure && (
            <DocIsDraftBadge approved_at={step.approved_at} />
          )}
          {actionButtons}
          {step.type !== 'ending' && step.type !== 'processprocedure' && (
            <StyledCollapseButton
              className={cs('btn btn-link btn-lg px-1 py-2 d-print-none', {
                'float-left': isRTLText,
                'float-right': !isRTLText,
              })}
              onClick={() => onToggleCollapse(step.id, !collapsed)}
            >
              <i
                className={cs('fa fa-fw', {
                  'fa-chevron-down': collapsed,
                  'fa-chevron-up': !collapsed,
                })}
              />
            </StyledCollapseButton>
          )}
        </>
      }
      title={
        <StyledTitleDiv>
          <div className="d-flex justify-content-between align-items-center">
            <WrapInLinkTag step={step}>
              <div className="d-flex" ref={stepTitleRef}>
                <HighlightableTextWrapper
                  objectId={objectId}
                  versionId={versionId}
                  stepId={`${HIGHLIGHT_STEP_TITLE_PREFIX[contentType]}-${step.id}`}
                  content={title}
                  contentType={contentType}
                  innerRef={stepTitleRef}
                />
                {showCopyLink && !('deleted' in step) && (
                  <CopyLinkButton
                    className="btn btn-link copy-link ml-1 py-0 px-1"
                    link={`#${step.id}`}
                    tooltip={gt('Copy Step Link')}
                  />
                )}
              </div>
            </WrapInLinkTag>
            {stepIsActiveProcedure && (
              <ProcedureStepPrivateStatus step={step} processURL={objectURL} />
            )}
          </div>
        </StyledTitleDiv>
      }
      afterTitleContent={
        <>
          <div className="collapsable-area" ref={collapsableEl}>
            {content ? (
              <StyledStepContentSeparatorDiv>
                <HighlightableTextWrapper
                  objectId={objectId}
                  versionId={versionId}
                  stepId={step.id}
                  content={content}
                  contentType={contentType}
                  innerRef={collapsableEl}
                />
              </StyledStepContentSeparatorDiv>
            ) : null}
            {carousel.length > 0 && (
              <ImagesCarousel images={carousel} className="step-images" />
            )}
            {fieldInputs}
            {choicesList}
            {automation}
            {'files' in step && step.files.length > 0 && (
              <ProcedureStepFiles files={step.files} />
            )}
          </div>
          {next}
        </>
      }
    />
  );
}

interface WrapInLinkTagProps {
  children: ReactNode;
  step:
    | ProcedureStepWithDiffType
    | ProcedureContentStepType
    | ProcedureDecisionStepType
    | ProcedureAutomationStepType
    | ProcedureEndingStepType
    | DeletedProcessProcedureAsStepSerializedType
    | AnyStepType
    | ProcessProcedureAsStepSerializedType
    | RegularProcessProcedureAsStepInTaskSerializedType;
}

function WrapInLinkTag({children, step}: WrapInLinkTagProps) {
  const isProcedureDeleted = step.type === 'processprocedure' && step.deleted;
  const linkClasses = cs('text-body d-block title-container', {
    unnamed: isProcedureDeleted,
  });

  return isProcedureDeleted ? (
    <i className={linkClasses}>
      <LazyTooltip
        tooltip={gt(
          'This procedure has been deleted. Remove it from the process and approve a new version.'
        )}
      >
        <span>{children}</span>
      </LazyTooltip>
    </i>
  ) : step.type === 'processprocedure' && !step.deleted ? (
    <Link
      to={step.html_url}
      className={cs(linkClasses, 'cursor-pointer', {
        'text-italic': step.title == '',
      })}
    >
      {children}
    </Link>
  ) : (
    <a href={'#' + step.id} className={linkClasses}>
      {children}
    </a>
  );
}

function animateCollapse(
  collapse: boolean,
  collapsableEl: Ref<HTMLDivElement>,
  disableAnimation = false
) {
  if (!collapsableEl.current) return;

  const command = collapse ? 'slideUp' : 'slideDown';
  jquery(collapsableEl.current)[command](
    disableAnimation ? 0 : COLLAPSE_DURATION_MS
  );
}

const StyledCollapseButton = styled.button`
  line-height: 1;
  height: 2rem;
  color: ${colors.lightGray};
  cursor: pointer;
  &:hover {
    color: ${colors.mediumGray};
  }
`;

const StyledTitleDiv = styled.div`
  .copy-link {
    display: none;
    font-size: 0.5em;
    color: ${colors.light};
  }

  &:hover {
    .copy-link {
      display: inline-block;
    }
  }

  a {
    user-select: text;
    -webkit-user-drag: none; /* Disables dragging in WebKit browsers (e.g., Chrome, Safari) */
  }
`;
